import { Table } from '@tanstack/react-table';
import { DownloadIcon, SearchIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import * as React from 'react';

import { Button } from '../button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../dropdown-menu';
import { Input } from '../input';

export interface FilterConfig {
  column: string;
  title: string;
  options?: { label: string; value: string }[];
}

export interface BulkAction<TData> {
  label: string;
  icon?: React.ReactNode;
  onClick: (selectedRows: TData[]) => void;
  variant?: 'default' | 'destructive';
}

export interface ExportConfig<TData> {
  csv?: boolean | { filename?: string };
  excel?: boolean | { filename?: string };
  onExport?: (format: 'csv' | 'excel', data: TData[]) => void;
}

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  searchConfig?: {
    placeholder?: string;
    column?: string;
    /** Controlled value for server-side search */
    value?: string;
    /** Callback for server-side search */
    onChange?: (value: string) => void;
  } | null;
  filters?: FilterConfig[];
  bulkActions?: BulkAction<TData>[];
  viewOptions?: boolean;
  exportConfig?: ExportConfig<TData>;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  /** Custom content to render on the left side of toolbar */
  leftSlot?: React.ReactNode;
  /** Custom content to render on the right side of toolbar */
  rightSlot?: React.ReactNode;
}

export function DataTableToolbar<TData>({
  table,
  searchConfig,
  filters = [],
  bulkActions = [],
  viewOptions = false,
  exportConfig,
  globalFilter,
  setGlobalFilter,
  leftSlot,
  rightSlot,
}: DataTableToolbarProps<TData>) {
  const hasColumnFilters = table.getState().columnFilters.length > 0;
  const isControlledSearch = !!searchConfig?.onChange;
  const hasSearchValue = isControlledSearch
    ? !!searchConfig?.value
    : searchConfig?.column
      ? !!(table.getColumn(searchConfig.column)?.getFilterValue() as string)
      : globalFilter.length > 0;
  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const hasSelectedRows = selectedRows.length > 0;

  const handleExport = React.useCallback(
    (format: 'csv' | 'excel') => {
      const data = table.getFilteredRowModel().rows.map(row => row.original);

      if (exportConfig?.onExport) {
        exportConfig.onExport(format, data);
        return;
      }

      // Default CSV export
      if (format === 'excel') {
        console.warn(
          'Excel export requires a custom onExport handler. Use csv or provide onExport.'
        );
        return;
      }

      if (format === 'csv') {
        const columns = table.getAllColumns().filter(col => col.getIsVisible());
        const headers = columns.map(col => col.id).join(',');
        const rows = data
          .map(row =>
            columns
              .map(col => {
                const value = (row as Record<string, unknown>)[col.id];
                // Escape quotes and wrap in quotes if contains comma
                const strValue = String(value ?? '');
                return strValue.includes(',') ? `"${strValue.replace(/"/g, '""')}"` : strValue;
              })
              .join(',')
          )
          .join('\n');

        const csvContent = `${headers}\n${rows}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const filename =
          typeof exportConfig?.csv === 'object' ? exportConfig.csv.filename || 'export' : 'export';
        link.href = URL.createObjectURL(blob);
        link.download = `${filename}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      }
    },
    [table, exportConfig]
  );

  const showExport = exportConfig?.csv || exportConfig?.excel;

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-dark-gray-600">
      <div className="flex flex-1 items-center gap-2 flex-wrap">
        {/* Left slot for custom content */}
        {leftSlot}

        {searchConfig && (
          <div className="relative w-full sm:w-auto">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400 dark:text-dark-gray-500" />
            <Input
              aria-label={searchConfig.placeholder || 'Search results'}
              placeholder={searchConfig.placeholder || 'Search…'}
              value={
                isControlledSearch
                  ? (searchConfig.value ?? '')
                  : searchConfig.column
                    ? ((table.getColumn(searchConfig.column)?.getFilterValue() as string) ?? '')
                    : globalFilter
              }
              onChange={event => {
                const value = event.target.value;
                if (isControlledSearch) {
                  searchConfig.onChange!(value);
                } else if (searchConfig.column) {
                  table.getColumn(searchConfig.column)?.setFilterValue(value);
                } else {
                  setGlobalFilter(value);
                }
              }}
              className="h-9 w-full sm:w-[250px] pl-8 pr-8 border-gray-200 dark:border-dark-gray-500 bg-gray-50 dark:bg-dark-gray-700 focus:bg-white dark:focus:bg-dark-gray-600"
            />
            {hasSearchValue && (
              <button
                type="button"
                onClick={() => {
                  if (isControlledSearch) {
                    searchConfig.onChange!('');
                  } else if (searchConfig.column) {
                    table.getColumn(searchConfig.column)?.setFilterValue('');
                  } else {
                    setGlobalFilter('');
                  }
                }}
                className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600 dark:text-dark-gray-500 dark:hover:text-dark-gray-300 transition-colors"
              >
                <XIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Filters would go here - simplified for now */}
        {filters.map(filter => {
          const column = table.getColumn(filter.column);
          if (!column) return null;

          return (
            <DropdownMenu key={filter.column}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9">
                  {filter.title}
                  {column.getFilterValue() !== undefined && (
                    <span className="ml-2 rounded-full bg-primary-100 dark:bg-primary-900 px-1.5 py-0.5 text-xs text-primary-700 dark:text-primary-300">
                      1
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>{filter.title}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {filter.options?.map(option => (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={column.getFilterValue() === option.value}
                    onCheckedChange={checked => {
                      column.setFilterValue(checked ? option.value : undefined);
                    }}
                  >
                    {option.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })}

        {hasColumnFilters && (
          <Button
            variant="ghost"
            onClick={() => {
              table.resetColumnFilters();
              setGlobalFilter('');
            }}
            className="h-9 px-2 lg:px-3"
          >
            Reset filters
            <XIcon className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Right slot for custom content */}
        {rightSlot}

        {/* Bulk actions when rows are selected */}
        {hasSelectedRows && bulkActions.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-dark-gray-400">
              {selectedRows.length} selected
            </span>
            {bulkActions.map(action => (
              <Button
                key={action.label}
                variant={action.variant === 'destructive' ? 'destructive' : 'outline'}
                size="sm"
                onClick={() => action.onClick(selectedRows.map(r => r.original))}
                className="h-9"
              >
                {action.icon}
                {action.label}
              </Button>
            ))}
          </div>
        )}

        {/* Export options */}
        {showExport && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                <DownloadIcon className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export data</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {exportConfig?.csv && (
                <DropdownMenuItem onClick={() => handleExport('csv')}>
                  Export as CSV
                </DropdownMenuItem>
              )}
              {exportConfig?.excel && (
                <DropdownMenuItem onClick={() => handleExport('excel')}>
                  Export as Excel
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* View options (column visibility) */}
        {viewOptions && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="ml-auto hidden lg:flex h-9">
                <SlidersHorizontalIcon className="mr-2 h-4 w-4" />
                View
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[150px]">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {table
                .getAllColumns()
                .filter(column => typeof column.accessorFn !== 'undefined' && column.getCanHide())
                .map(column => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={value => column.toggleVisibility(!!value)}
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
